import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { projectService } from "../services/projectService";
import { rbacService } from "../services/rbacService";
import { userService } from "../services/userService";
import { ArrowLeft, Save, UserPlus, X, Pencil } from "lucide-react";
import FormSkeleton from "../components/skeletons/FormSkeleton";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import toast from "react-hot-toast";

const ProjectSettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const projectId = searchParams.get("project_id");
  const clientId = searchParams.get("client_id") || user?.client_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [projectTypes, setProjectTypes] = useState([]);

  // View vs edit mode for Project Details (default: view mode)
  const [isEditingProject, setIsEditingProject] = useState(false);

  // Project form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    project_type: "",
  });

  // User management
  const [projectMembers, setProjectMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [userRoles, setUserRoles] = useState({});
  const [userSearchQuery, setUserSearchQuery] = useState("");

  // Load project details
  const loadProject = useCallback(async () => {
    if (!projectId || !clientId) return;

    try {
      setLoading(true);
      const response = await projectService.getProject(projectId, clientId);

      if (response.success) {
        const projectData = response.data;
        setProject(projectData);
        setFormData({
          name: projectData.name || "",
          description: projectData.description || "",
          project_type: projectData.type || projectData.project_type || "",
        });
      } else {
        toast.error(response.message || "Project not found");
        navigate("/test-cases");
      }
    } catch (error) {
      console.error("Error loading project:", error);
      toast.error(
        error.response?.data?.message || "Failed to load project details",
      );
      navigate("/test-cases");
    } finally {
      setLoading(false);
    }
  }, [projectId, clientId, navigate]);

  // Load project types
  const loadProjectTypes = useCallback(async () => {
    try {
      const response = await projectService.getProjectTypes();
      if (response.success) {
        setProjectTypes(response.data || []);
      }
    } catch (error) {
      console.error("Error loading project types:", error);
    }
  }, []);

  // Load project members
  const loadProjectMembers = useCallback(async () => {
    if (!projectId || !clientId) return;

    try {
      const response = await projectService.getProjectUsers(
        projectId,
        clientId,
      );
      if (response.success) {
        setProjectMembers(response.data?.users || []);
      }
    } catch (error) {
      console.error("Error loading project members:", error);
      toast.error("Failed to load project members");
    }
  }, [projectId, clientId]);

  // Load available users (client users not in project)
  const loadAvailableUsers = useCallback(async () => {
    if (!clientId) return;

    try {
      const response = await userService.findUsers(
        { client_id: clientId },
        1,
        1000,
      );
      if (response.success) {
        const allUsers = response.data?.users || response.data || [];
        // Filter out users already in project
        const memberUserIds = new Set(projectMembers.map((m) => m.user_id));
        const available = allUsers.filter((u) => !memberUserIds.has(u.user_id));
        setAvailableUsers(available);
      }
    } catch (error) {
      console.error("Error loading available users:", error);
    }
  }, [clientId, projectMembers]);

  // Load roles - only client-specific roles for project assignment
  const loadRoles = useCallback(async () => {
    if (!clientId) return;

    try {
      const response = await rbacService.getRoles({
        client_id: clientId,
        is_active: true,
      });
      if (response.success) {
        const rolesList = response.data?.roles || response.data || [];
        // System/global roles to exclude - these shouldn't be assignable to project members
        const systemRoles = [
          "chief-admin",
          "chief_admin",
          "chiefadmin",
          "super-admin",
          "super_admin",
          "superadmin",
        ];

        const filteredRoles = (
          Array.isArray(rolesList) ? rolesList : []
        ).filter((role) => {
          // Must be active and not deleted
          if (!role.is_active || role.is_deleted) return false;
          // Must belong to this specific client (not a global/system role)
          if (!role.client_id || role.client_id !== clientId) return false;
          // Exclude system roles
          const roleLower = (role.role_name || "")
            .toLowerCase()
            .replace(/[\s_-]/g, "");
          if (systemRoles.some((sr) => sr.replace(/[\s_-]/g, "") === roleLower))
            return false;
          return true;
        });
        setRoles(filteredRoles);
      }
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  }, [clientId]);

  useEffect(() => {
    if (!projectId) {
      toast.error("Project ID is required");
      navigate("/test-cases");
      return;
    }

    loadProject();
    loadProjectTypes();
    loadRoles();
  }, [projectId, loadProject, loadProjectTypes, loadRoles, navigate]);

  // Reset to view mode when project or projectId changes (e.g. navigating to this page)
  useEffect(() => {
    setIsEditingProject(false);
  }, [projectId]);

  useEffect(() => {
    loadProjectMembers();
  }, [loadProjectMembers]);

  useEffect(() => {
    if (showAddUserModal) {
      loadAvailableUsers();
    }
  }, [showAddUserModal, loadAvailableUsers]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle project update
  const handleUpdateProject = async (e) => {
    e.preventDefault();

    if (!projectId || !clientId) return;

    try {
      setSaving(true);
      const updateData = {
        project_id: projectId,
        name: formData.name,
        description: formData.description,
        project_type: formData.project_type,
      };

      const response = await projectService.updateProject(updateData);

      if (response.success) {
        toast.success("Project updated successfully");
        setIsEditingProject(false);
        // Reload project to get updated data
        await loadProject();
      } else {
        toast.error(response.message || "Failed to update project");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error(error.response?.data?.message || "Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (project) {
      setFormData({
        name: project.name || "",
        description: project.description || "",
        project_type: project.type || project.project_type || "",
      });
    }
    setIsEditingProject(false);
  };

  // Handle user selection
  const handleUserToggle = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
      const newUserRoles = { ...userRoles };
      delete newUserRoles[userId];
      setUserRoles(newUserRoles);
    } else {
      newSelected.add(userId);
      // Set default role (first role or user's current role)
      const user = availableUsers.find((u) => u.user_id === userId);
      const rolesList = Array.isArray(roles) ? roles : [];
      const defaultRole =
        rolesList.find(
          (r) => r.role_name?.toLowerCase() === user?.role?.toLowerCase(),
        ) || rolesList[0];
      if (defaultRole) {
        setUserRoles((prev) => ({
          ...prev,
          [userId]: defaultRole.role_id,
        }));
      }
    }
    setSelectedUsers(newSelected);
  };

  // Handle role change for user
  const handleRoleChange = (userId, roleId) => {
    setUserRoles((prev) => ({
      ...prev,
      [userId]: roleId,
    }));
  };

  // Handle add users to project
  const handleAddUsers = async () => {
    if (!projectId || !clientId || selectedUsers.size === 0) return;

    try {
      setSaving(true);
      const members = Array.from(selectedUsers)
        .map((userId) => ({
          user_id: userId,
          role_id: userRoles[userId],
        }))
        .filter((m) => m.role_id); // Only include users with roles

      if (members.length === 0) {
        toast.error("Please assign roles to selected users");
        return;
      }

      const response = await projectService.assignProjectUsers(
        projectId,
        clientId,
        members,
      );

      if (response.success) {
        toast.success("Users added to project successfully");
        setShowAddUserModal(false);
        setSelectedUsers(new Set());
        setUserRoles({});
        setUserSearchQuery("");
        await loadProjectMembers();
        await loadAvailableUsers();
      } else {
        toast.error(response.message || "Failed to add users to project");
      }
    } catch (error) {
      console.error("Error adding users:", error);
      toast.error(
        error.response?.data?.message || "Failed to add users to project",
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle remove user from project
  const handleRemoveUser = async (userRoleMapId, userId) => {
    if (!clientId) return;

    try {
      setSaving(true);
      const response = await rbacService.removeRoleFromUser(
        userId,
        userRoleMapId,
        clientId,
      );

      if (response.success) {
        toast.success("User removed from project");
        await loadProjectMembers();
        await loadAvailableUsers();
      } else {
        toast.error(response.message || "Failed to remove user");
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error("Failed to remove user from project");
    } finally {
      setSaving(false);
    }
  };

  // Get filtered users
  const getFilteredUsers = () => {
    if (!userSearchQuery.trim()) {
      return availableUsers;
    }
    const query = userSearchQuery.toLowerCase();
    return availableUsers.filter(
      (u) =>
        u.full_name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query),
    );
  };

  if (loading) {
    return (
      <div className="min-h-[400px] p-4">
        <FormSkeleton fields={8} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-6">
        <div className="card p-6">
          <p className="text-text-secondary">Project not found</p>
        </div>
      </div>
    );
  }

  const filteredUsers = getFilteredUsers();

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              navigate(`/test-cases?${params.toString()}`);
            }}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded-md transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              Project Settings
            </h1>
            <p className="text-text-secondary mt-1">{project.name}</p>
          </div>
        </div>
      </div>

      {/* Project Details Section */}
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-text-primary">
            Project Details
          </h2>
          {!isEditingProject ? (
            <Button
              type="button"
              variant="default"
              onClick={() => setIsEditingProject(true)}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="project-details-form"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <InlineLoadSkeleton className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <form
          id="project-details-form"
          onSubmit={handleUpdateProject}
          className="space-y-4"
        >
          <fieldset
            disabled={!isEditingProject}
            className="space-y-4 border-0 p-0 m-0 min-w-0"
          >
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Project Name *
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Description
              </label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Project Type
              </label>
              <Select
                value={formData.project_type}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, project_type: value }))
                }
                disabled={!isEditingProject}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent>
                  {projectTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </fieldset>
        </form>
      </div>

      {/* Project Users Section */}
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-text-primary">
            Project Members
          </h2>
          <Button onClick={() => setShowAddUserModal(true)} disabled={saving}>
            <UserPlus className="w-4 h-4 mr-2" />
            Add Members
          </Button>
        </div>

        {projectMembers.length === 0 ? (
          <p className="text-text-secondary py-4">
            No members assigned to this project
          </p>
        ) : (
          <div className="space-y-2">
            {projectMembers.map((member) => (
              <div
                key={member.user_role_map_id || member.user_id}
                className="flex justify-between items-center p-3 border border-border-primary rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700">
                      {member.full_name?.charAt(0)?.toUpperCase() || "U"}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-text-primary">
                      {member.full_name}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {member.email}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-text-secondary">
                    {member.role_name || "No role"}
                  </span>
                  <button
                    onClick={() =>
                      handleRemoveUser(member.user_role_map_id, member.user_id)
                    }
                    disabled={saving}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                    title="Remove user"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-primary rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-text-primary">
                Add Members to Project
              </h3>
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setSelectedUsers(new Set());
                  setUserRoles({});
                  setUserSearchQuery("");
                }}
                className="p-1 text-text-secondary hover:text-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <Input
                placeholder="Search users..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>

            {filteredUsers.length === 0 ? (
              <p className="text-text-secondary py-4 text-center">
                No available users
              </p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredUsers.map((user) => {
                  const isSelected = selectedUsers.has(user.user_id);
                  const userRoleId = userRoles[user.user_id];

                  return (
                    <div
                      key={user.user_id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        isSelected
                          ? "border-primary-500 bg-primary-50"
                          : "border-border-primary"
                      }`}
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleUserToggle(user.user_id)}
                          className="w-4 h-4"
                        />
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700">
                            {user.full_name?.charAt(0)?.toUpperCase() || "U"}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-text-primary">
                            {user.full_name}
                          </div>
                          <div className="text-sm text-text-secondary">
                            {user.email}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <Select
                          value={userRoleId || undefined}
                          onValueChange={(roleId) =>
                            handleRoleChange(user.user_id, roleId)
                          }
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map((role) => (
                              <SelectItem
                                key={role.role_id}
                                value={role.role_id}
                              >
                                {role.role_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-border-primary">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddUserModal(false);
                  setSelectedUsers(new Set());
                  setUserRoles({});
                  setUserSearchQuery("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddUsers}
                disabled={saving || selectedUsers.size === 0}
              >
                {saving ? (
                  <>
                    <InlineLoadSkeleton className="h-4 w-4 mr-2" />
                    Add Selected ({selectedUsers.size})
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Selected ({selectedUsers.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSettingsPage;
